import React, { useState, useEffect, ChangeEvent } from 'react';
import omit from 'lodash/omit';
import { getDayOfYear, format } from 'date-fns';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDatePicker from '@mui/lab/StaticDatePicker';
import TextField from '@mui/material/TextField';
import Badge, { BadgeTypeMap } from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import PickersDay from '@mui/lab/PickersDay/PickersDay';
import Sidebar from '../../components/Sidebar/Sidebar';
import { connect, useDispatch } from 'react-redux';
import { AppState } from '../../store/rootReducer';
import { getUserId } from '../../store/auth/selectors';
import SidebarSecond from '../../components/Sidebar/SidebarSecond';
import strings from '../../resources/strings';
import { requestGetList } from '../../store/languages/actions';
import { getLanguagesList } from '../../store/languages/selectors';
import { requestGetListCoach, requestUpdateCoachAvailableTime } from '../../store/coach/actions';
import { requestUpdateMeetingNote } from '../../store/meetings/actions';
import { getMeetingsList } from '../../store/meetings/selectors';
import { AvailableTimeMap, AvailableTime } from '../../store/coach/types';
import SnackbarMessage from '../../components/SnackbarMessage/SnackbarMessage';
import { getLoggedCoach } from '../../store/coach/selectors';
import { getError } from '../../store/coach/selectors';
import { MeetingsCategory, MeetingStatus, MeetingsProps } from './MeetingsPageInterfaces';
import { requestGetListMeetings } from '../../store/meetings/actions';
import { MeetingsMap } from '../../store/meetings/types';
import { getAvailableTimeMapKey } from '../../store/coach/utils';
import { getTimeSlots, isWeekend } from './utils';
import consts from './consts';

const MAX_NOTE_LENGTH = 2000;

const MeetingsPage: React.FC<MeetingsProps> = (props) => {
    const [error, setError] = useState(false);
    const [meetingsTypes, setMeetingsTypes] = useState<MeetingsCategory[]>([]);
    const [selectedType, setSelectedType] = useState<MeetingsCategory>();
    const [availableTimeMap, setAvailableTimeMap] = useState<AvailableTimeMap>({});
    const [successMessage, setSuccessMessage] = useState('');
    const [calendarDate, setCalendarDate] = useState<Date | null>(new Date());
    const [meetingsNotes, setMeetingsNotes] = useState<MeetingsMap[MeetingStatus.Confirmed]>();
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<AvailableTime>();
    const [note, setNote] = useState('');
    const [noteError, setNoteError] = useState<string>('');

    const dispatch = useDispatch();

    const isTimeSlotIncluded = (timeSlot: AvailableTime) => {
        const mapKey = getAvailableTimeMapKey(timeSlot.date);
        return Boolean(availableTimeMap?.[mapKey]?.find(({ time }) => time === timeSlot.time));
    };

    const showNotesInput = selectedType?.id === MeetingStatus.Confirmed;

    const isTimeSlotDisabled = (timeSlot: AvailableTime) => {
        if (selectedType?.id === MeetingStatus.New) {
            const year = timeSlot.date.getFullYear();
            const dayMapKey = String(getDayOfYear(timeSlot.date)) + String(year);
            const slots = props.meetings[MeetingStatus.Confirmed][dayMapKey];
            return Boolean(slots?.find(({ time }) => time === timeSlot.time));
        }
        return false;
    };

    const getMeetingByTimeSlot = (timeSlot?: AvailableTime) => {
        if (timeSlot == null) return;
        const meetingHours: string[] = timeSlot?.time.split('-') ? timeSlot?.time.split('-') : [''];
        const endTime: number = parseInt(meetingHours?.[1]);

        const timeslotDate: Date = timeSlot.date;
        timeslotDate.setHours(endTime);
        timeslotDate.setMinutes(0);
        timeslotDate.setSeconds(0);

        const currentDate: Date = new Date();

        if (selectedType?.id !== MeetingStatus.Confirmed || !timeSlot || timeslotDate > currentDate) return;

        const mapKey = getAvailableTimeMapKey(timeSlot?.date);
        const meeting = meetingsNotes?.[mapKey]?.find(({ time }) => time === timeSlot?.time);

        return meeting;
    };

    const getStatusData = (id?: MeetingStatus) => {
        return id ? consts.status[id] : consts.status.new;
    };

    const getChipColor = (timeSlot: AvailableTime) => {
        if (!isTimeSlotIncluded(timeSlot)) return 'default';
        if (selectedType?.id === MeetingStatus.Cancelled) return 'error';
        if (selectedType?.id === MeetingStatus.Confirmed) return 'success';
        return 'primary';
    };

    const isMeetingStatusNew = (id?: MeetingStatus) => id === MeetingStatus.New;

    const handleSelectTimeSlot = (timeSlot: AvailableTime) => {
        setAvailableTimeMap((prevState) => {
            const mapKey = getAvailableTimeMapKey(timeSlot.date);
            if (isTimeSlotIncluded(timeSlot)) {
                const newTimeSlots = prevState[mapKey].filter(({ time }) => time !== timeSlot.time);
                return newTimeSlots.length ? { ...prevState, [mapKey]: newTimeSlots } : omit(prevState, mapKey);
            }
            return { ...prevState, [mapKey]: [...(prevState[mapKey] || []), timeSlot] };
        });
    };

    const handleChangeNote = (event: ChangeEvent<HTMLInputElement>) => {
        setNoteError('');
        if (event.target.value.length >= MAX_NOTE_LENGTH) {
            setNoteError(strings.coachPage.meetings.maxLengthNote);
            return;
        }
        setNote(event.target.value);
    };

    const handleSelectCurrentType = (id: string) => {
        setCalendarDate(null);
        setSelectedType(meetingsTypes.find((type) => type.id === id));
    };

    const handleSave = () => {
        dispatch(requestUpdateCoachAvailableTime(props.userId, availableTimeMap));
        setSuccessMessage(strings.coachPage.meetings.successMessage);
    };

    const handleSaveNote = () => {
        const meetingId = getMeetingByTimeSlot(selectedTimeSlot)?.id;
        if (!meetingId) return;

        dispatch(requestUpdateMeetingNote(meetingId, note));
        setSuccessMessage(strings.coachPage.meetings.successMessageSaveNote);
    };

    const showMeetings = selectedType && props.coach;
    const showDisableInfo = props.coach === undefined;

    useEffect(() => {
        dispatch(requestGetListCoach());
        dispatch(requestGetList());
        dispatch(requestGetListMeetings(props.userId));
        setMeetingsTypes(consts.meetingTypeList);
    }, []);

    useEffect(() => {
        props.error && setError(true);
    }, [props.error]);

    useEffect(() => {
        props.meetings && setMeetingsNotes(props.meetings[MeetingStatus.Confirmed]);
    }, [props.meetings]);

    useEffect(() => {
        setNoteError('');
        const meetingNote = getMeetingByTimeSlot(selectedTimeSlot)?.meetingNote || '';
        setNote(meetingNote);
    }, [selectedTimeSlot]);

    useEffect(() => {
        if (selectedType?.id) {
            setError(false);
            setSuccessMessage('');
            setCalendarDate(consts.currentDate);
        }
    }, [selectedType?.id]);

    useEffect(() => {
        if (!selectedType?.id) return;

        // Set timeSlots for NEW
        if (selectedType?.id === MeetingStatus.New) {
            return setAvailableTimeMap(props.coach?.availableTime || {});
        }
        // Set timeSlots for Cancelled or Confirmed
        const timeSlots = props.meetings[selectedType.id] as AvailableTimeMap;
        setAvailableTimeMap(timeSlots || {});
    }, [selectedType?.id]);

    useEffect(() => {
        props.coach && setAvailableTimeMap(props.coach.availableTime);
        meetingsTypes && setSelectedType(meetingsTypes[0]);
    }, [props.coach, meetingsTypes]);

    return (
        <div className="content">
            <Sidebar />
            {showMeetings && (
                <SidebarSecond
                    hideStatus
                    actualItemId={selectedType?.id}
                    actualLanguage={consts.defaultUILanguage}
                    data={meetingsTypes}
                    title={strings.coachPage.meetings.title}
                    setActualItemId={handleSelectCurrentType}
                />
            )}

            <Box className="main">
                {showDisableInfo && (
                    <Box sx={{ pt: 2 }}>
                        <Alert variant="outlined" severity="info" sx={{ fontSize: 14, maxWidth: 800 }}>
                            {strings.coachPage.meetings.noCoachNotification}
                        </Alert>
                    </Box>
                )}

                {showMeetings && (
                    <Box className="main">
                        <Box sx={{ marginTop: '2rem', pt: 2.5, pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                                {/* Calendar */}
                                <Box sx={{ mr: 4 }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 'bold' }} component="h3">
                                        {getStatusData(selectedType.id).title}
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <StaticDatePicker
                                            autoFocus
                                            minDate={isMeetingStatusNew(selectedType.id) ? consts.minDate : undefined}
                                            disablePast={isMeetingStatusNew(selectedType.id)}
                                            maxDate={consts.maxDate}
                                            shouldDisableDate={isWeekend}
                                            displayStaticWrapperAs="desktop"
                                            value={calendarDate}
                                            onChange={setCalendarDate}
                                            renderInput={(params) => <TextField {...params} />}
                                            renderDay={(day, _value, DayComponentProps) => {
                                                const mapKey = String(getAvailableTimeMapKey(day));
                                                let isSelected = false;

                                                // Set selected days for NEW
                                                if (selectedType.id === MeetingStatus.New)
                                                    isSelected = Object.keys(availableTimeMap || {}).includes(mapKey);

                                                // Set selected days for CANCELLED or CONFIRMED
                                                if (selectedType.id !== MeetingStatus.New)
                                                    isSelected = Object.keys(
                                                        props.meetings[selectedType.id] || {},
                                                    ).includes(mapKey);

                                                if (!isSelected || DayComponentProps.outsideCurrentMonth) {
                                                    return <PickersDay {...DayComponentProps} />;
                                                }

                                                return (
                                                    <Badge
                                                        key={DayComponentProps.key}
                                                        color={
                                                            getStatusData(selectedType.id)
                                                                .color as BadgeTypeMap['props']['color']
                                                        }
                                                        variant="dot"
                                                        overlap="circular"
                                                    >
                                                        <PickersDay {...DayComponentProps} />
                                                    </Badge>
                                                );
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                {/* Times grid */}
                                <Box sx={{ flexShrink: 0 }}>
                                    {calendarDate && (
                                        <>
                                            <Typography sx={{ fontSize: 16, fontWeight: 'bold', mb: 2 }} component="h3">
                                                {format(calendarDate, 'MMMM do, yyyy')}
                                            </Typography>
                                            <Grid
                                                container
                                                sx={{ maxWidth: 400 }}
                                                spacing={2}
                                                columns={{ xs: 4, sm: 8, md: 12 }}
                                            >
                                                {getTimeSlots(8, 12, calendarDate).map((timeSlot) => (
                                                    <Grid item xs={2} sm={4} md={4} key={timeSlot.id}>
                                                        {Boolean(getMeetingByTimeSlot(timeSlot)) && (
                                                            <Badge
                                                                sx={{ width: '100%' }}
                                                                key={timeSlot.time}
                                                                badgeContent={<ChatBubbleIcon color="info" />}
                                                            >
                                                                <Chip
                                                                    label={timeSlot.time}
                                                                    color="success"
                                                                    variant="filled"
                                                                    sx={{
                                                                        fontSize: 12,
                                                                        borderRadius: 1,
                                                                        width: '100%',
                                                                    }}
                                                                    onClick={() => setSelectedTimeSlot(timeSlot)}
                                                                />
                                                            </Badge>
                                                        )}

                                                        {!Boolean(getMeetingByTimeSlot(timeSlot)) && (
                                                            <Chip
                                                                label={timeSlot.time}
                                                                color={getChipColor(timeSlot)}
                                                                variant={
                                                                    isTimeSlotIncluded(timeSlot) ? 'filled' : 'outlined'
                                                                }
                                                                sx={{ fontSize: 12, borderRadius: 1, width: '100%' }}
                                                                disabled={
                                                                    !isMeetingStatusNew(selectedType?.id) ||
                                                                    isTimeSlotDisabled(timeSlot)
                                                                }
                                                                onClick={() => handleSelectTimeSlot(timeSlot)}
                                                            />
                                                        )}
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {/* Notes input */}
                            {showNotesInput && (
                                <Box>
                                    <Typography sx={{ fontSize: 16, fontWeight: 'bold', mb: 2 }} component="h3">
                                        {strings.coachPage.meetings.notesLabel}
                                    </Typography>
                                    <TextField
                                        multiline
                                        value={note}
                                        onChange={handleChangeNote}
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                width: '40rem',
                                                height: '12rem!important',
                                                maxHeight: '100%!important',
                                                '&:hover, &:focus': { border: 'none' },
                                            },
                                        }}
                                    />
                                    {noteError.length > 0 && (
                                        <Typography
                                            sx={{ fontSize: 10, fontWeight: 'bold', mb: 2, color: 'red' }}
                                            component="h6"
                                        >
                                            {noteError}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Success message */}
                        {Boolean(successMessage.length) && (
                            <Box>
                                <SnackbarMessage
                                    serverity="success"
                                    message={successMessage}
                                    clearMessage={() => {
                                        setSuccessMessage('');
                                    }}
                                />
                            </Box>
                        )}

                        {/* Error message */}
                        {error && (
                            <SnackbarMessage
                                serverity="error"
                                message={error ? strings.coachPage.errors.getMeetings : ''}
                                clearMessage={() => setError(false)}
                            />
                        )}

                        {/* Buttons */}
                        {(isMeetingStatusNew(selectedType?.id) || showNotesInput) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '1rem',
                                    paddingRight: '2rem',
                                    marginTop: '2rem',
                                }}
                            >
                                <Button
                                    startIcon={<SaveIcon />}
                                    variant="outlined"
                                    sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                                    onClick={showNotesInput ? handleSaveNote : handleSave}
                                >
                                    {showNotesInput
                                        ? strings.newsAndEvents.buttons.saveNote
                                        : strings.newsAndEvents.buttons.save}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </div>
    );
};

function mapStateToProps(state: AppState) {
    return {
        userId: getUserId(state),
        languages: getLanguagesList(state),
        coach: getLoggedCoach(state),
        meetings: getMeetingsList(state),
        error: getError(state),
    };
}

export default connect(mapStateToProps)(MeetingsPage);
